<script setup lang="ts">
import { ref, computed, onMounted } from "vue";

type Channel = "whatsapp" | "telegram" | "email" | "phone";

const { t, locale } = useI18n();
const route = useRoute();
const { trackEvent } = useAnalytics();

const name = ref("");
const phone = ref("");
const email = ref("");
const message = ref("");
const channels = ref<Channel[]>([]);
const honeypot = ref("");

const status = ref<"idle" | "submitting" | "success" | "error">("idle");
const errorMessage = ref<string>("");
const fieldErrors = ref<{ name?: string; phone?: string; email?: string }>({});

const mountedAt = ref(0);
onMounted(() => {
  mountedAt.value = Date.now();
});

const channelOptions: { value: Channel; icon: string }[] = [
  { value: "whatsapp", icon: "mdi:whatsapp" },
  { value: "telegram", icon: "mdi:telegram" },
  { value: "email", icon: "mdi:email-outline" },
  { value: "phone", icon: "mdi:phone-outline" },
];

const showEmail = computed(() => channels.value.includes("email"));

function toggleChannel(c: Channel) {
  const idx = channels.value.indexOf(c);
  if (idx >= 0) channels.value.splice(idx, 1);
  else channels.value.push(c);
}

function countDigits(s: string): number {
  let n = 0;
  for (const c of s) if (c >= "0" && c <= "9") n++;
  return n;
}

function validate(): boolean {
  fieldErrors.value = {};
  let ok = true;
  if (name.value.trim().length < 1) {
    fieldErrors.value.name = t("contact_form.validation.name_required");
    ok = false;
  }
  if (countDigits(phone.value) < 7) {
    fieldErrors.value.phone = t("contact_form.validation.phone_invalid");
    ok = false;
  }
  if (showEmail.value && email.value.trim() === "") {
    fieldErrors.value.email = t("contact_form.validation.email_required_when_channel");
    ok = false;
  } else if (email.value.trim() !== "" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value.trim())) {
    fieldErrors.value.email = t("contact_form.validation.email_invalid");
    ok = false;
  }
  return ok;
}

// Inline feedback on phone blur: flag a non-empty too-short number, clear a
// prior error once valid. Empty-on-blur isn't an error yet (submit catches it).
function validatePhone() {
  if (phone.value && countDigits(phone.value) < 7) {
    fieldErrors.value.phone = t("contact_form.validation.phone_invalid");
  } else if (fieldErrors.value.phone) {
    fieldErrors.value.phone = undefined;
  }
}

async function onSubmit() {
  if (status.value === "submitting") return;
  if (honeypot.value.trim() !== "") return;
  if (Date.now() - mountedAt.value < 1500) return;
  if (!validate()) return;

  status.value = "submitting";
  errorMessage.value = "";

  // Single id used for both the server-side CAPI mirror (sent in payload)
  // and the client-side fbq event below — Meta dedupes by matching event_id.
  const metaEventId =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  const metaTestEventCode = readMetaTestCode();

  // GA4 client_id — same pattern as BookingRequestPanel: lets the server
  // send a Measurement Protocol `purchase` for this session if the lead
  // converts to a booking. Best-effort with internal timeout, never blocks.
  const gaClientId = await readGaClientId(
    (useRuntimeConfig().public as { ga4Id?: string }).ga4Id ?? "",
  );

  try {
    await $fetch("/api/contact", {
      method: "POST",
      body: {
        name: name.value.trim(),
        phone: phone.value.trim(),
        email: email.value.trim() || null,
        message: message.value.trim() || null,
        preferredChannels: channels.value,
        locale: locale.value,
        pageUrl: route.fullPath,
        website: honeypot.value,
        attribution: { ...(readAttribution() ?? {}), ga_client_id: gaClientId },
        metaEventId,
        ...(metaTestEventCode ? { metaTestEventCode } : {}),
      },
    });
    status.value = "success";
    trackEvent("contact_form_submit", {
      placement: "contact_page",
      channels: channels.value.join(","),
      meta_event_id: metaEventId,
    });
  } catch (e) {
    const err = e as { statusCode?: number; data?: { statusMessage?: string }; statusMessage?: string };
    if (err.statusCode === 429) {
      errorMessage.value = t("contact_form.error_rate_limit");
    } else {
      errorMessage.value = err.data?.statusMessage || err.statusMessage || t("contact_form.error_generic");
    }
    status.value = "error";
  }
}

function reset() {
  name.value = "";
  phone.value = "";
  email.value = "";
  message.value = "";
  channels.value = [];
  fieldErrors.value = {};
  errorMessage.value = "";
  status.value = "idle";
  mountedAt.value = Date.now();
}

// text-[16px] (not text-base — project overrides --text-base to 15px in main.css)
// prevents iOS Safari zoom-on-focus, which kicks in below 16px.
const inputClass =
  "w-full px-4 h-11 rounded-lg border bg-white dark:bg-slate-900 text-[16px] outline-none transition focus:ring-2 focus:ring-red-500/30";
const inputBorder = "border-slate-200 dark:border-slate-700 focus:border-red-400";
const inputBorderError = "border-red-400 focus:border-red-500";
</script>

<template>
  <div
    data-reveal
    class="rounded-2xl bg-white dark:bg-slate-800/40 ring-1 ring-slate-900/5 dark:ring-white/10 shadow-sm p-6 md:p-8"
    aria-labelledby="contact-form-heading"
  >
    <h2
      id="contact-form-heading"
      class="sr-only"
    >
      {{ t("contact_form.heading") }}
    </h2>
    <div>
            <Transition name="fade" mode="out-in">
              <div v-if="status === 'success'" key="success" class="text-center py-10">
                <div
                  class="inline-flex items-center justify-center size-14 rounded-full bg-emerald-500/10 text-emerald-500 mb-4"
                >
                  <Icon name="mdi:check-circle-outline" size="40px" />
                </div>
                <h3 class="text-xl font-semibold text-slate-900 dark:text-white">
                  {{ t("contact_form.success_heading") }}
                </h3>
                <p class="mt-2 text-slate-600 dark:text-slate-400">
                  {{ t("contact_form.success_subtitle") }}
                </p>
                <button
                  type="button"
                  class="mt-5 text-sm font-semibold text-red-500 hover:text-red-600"
                  @click="reset"
                >
                  {{ t("contact_form.success_again") }} →
                </button>
              </div>

              <form
                v-else
                key="form"
                novalidate
                class="space-y-5"
                @submit.prevent="onSubmit"
              >
                <input
                  v-model="honeypot"
                  type="text"
                  name="website"
                  tabindex="-1"
                  autocomplete="off"
                  aria-hidden="true"
                  class="absolute left-[-9999px] w-px h-px opacity-0 pointer-events-none"
                >

                <div class="grid gap-5 sm:grid-cols-2">
                  <label class="block">
                    <span class="block text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-400 mb-1.5">
                      {{ t("contact_form.name_label") }}
                    </span>
                    <input
                      v-model="name"
                      type="text"
                      autocomplete="name"
                      :placeholder="t('contact_form.name_placeholder')"
                      :class="[inputClass, fieldErrors.name ? inputBorderError : inputBorder]"
                    >
                    <span
                      v-if="fieldErrors.name"
                      class="block mt-1 text-xs text-red-500"
                    >
                      {{ fieldErrors.name }}
                    </span>
                  </label>

                  <label class="block">
                    <span class="block text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-400 mb-1.5">
                      {{ t("contact_form.phone_label") }}
                    </span>
                    <PhoneField
                      v-model="phone"
                      :error="!!fieldErrors.phone"
                      :placeholder="t('contact_form.phone_placeholder')"
                      :aria-label="t('contact_form.phone_label')"
                      @blur="validatePhone"
                    />
                    <span
                      v-if="fieldErrors.phone"
                      class="block mt-1 text-xs text-red-500"
                    >
                      {{ fieldErrors.phone }}
                    </span>
                  </label>
                </div>

                <fieldset>
                  <legend class="block text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-400 mb-2">
                    {{ t("contact_form.channels_heading") }}
                  </legend>
                  <div class="flex flex-wrap gap-2">
                    <button
                      v-for="opt in channelOptions"
                      :key="opt.value"
                      type="button"
                      :aria-pressed="channels.includes(opt.value)"
                      class="inline-flex items-center gap-2 h-10 px-4 rounded-full text-sm font-medium ring-1 transition select-none"
                      :class="
                        channels.includes(opt.value)
                          ? 'bg-red-500/10 ring-red-500 text-red-600 dark:text-red-400'
                          : 'bg-white dark:bg-slate-900 ring-slate-200 dark:ring-slate-700 text-slate-700 dark:text-slate-300 hover:ring-slate-300 dark:hover:ring-slate-600'
                      "
                      @click="toggleChannel(opt.value)"
                    >
                      <Icon :name="opt.icon" size="18px" />
                      {{ t(`contact_form.channels.${opt.value}`) }}
                    </button>
                  </div>
                </fieldset>

                <Transition name="fade">
                  <label v-if="showEmail" class="block">
                    <span class="block text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-400 mb-1.5">
                      {{ t("contact_form.email_label") }}
                    </span>
                    <input
                      v-model="email"
                      type="email"
                      autocomplete="email"
                      :placeholder="t('contact_form.email_placeholder')"
                      :class="[inputClass, fieldErrors.email ? inputBorderError : inputBorder]"
                    >
                    <span
                      v-if="fieldErrors.email"
                      class="block mt-1 text-xs text-red-500"
                    >
                      {{ fieldErrors.email }}
                    </span>
                  </label>
                </Transition>

                <label class="block">
                  <span class="block text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-400 mb-1.5">
                    {{ t("contact_form.message_label") }}
                  </span>
                  <textarea
                    v-model="message"
                    rows="4"
                    :placeholder="t('contact_form.message_placeholder')"
                    class="w-full px-4 py-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-[16px] outline-none transition focus:ring-2 focus:ring-red-500/30 focus:border-red-400 resize-y"
                  />
                </label>

                <div
                  v-if="status === 'error' && errorMessage"
                  class="p-3 rounded-lg bg-red-500/10 text-red-600 text-sm"
                >
                  {{ errorMessage }}
                </div>

                <button
                  type="submit"
                  class="inline-flex items-center justify-center gap-2 h-12 px-8 rounded-lg bg-red-500 hover:bg-red-600 disabled:opacity-50 disabled:pointer-events-none text-white font-semibold shadow-lg shadow-red-500/30 transition transform hover:-translate-y-0.5"
                  :disabled="status === 'submitting'"
                >
                  <Icon
                    v-if="status === 'submitting'"
                    name="mdi:loading"
                    size="20px"
                    class="animate-spin"
                  />
                  {{ status === "submitting" ? t("contact_form.submitting") : t("contact_form.submit") }}
                </button>
              </form>
            </Transition>
    </div>
  </div>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(6px);
}
</style>
